import { MonitorStatus } from '@fe/core';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SvgIcon } from '@progress/kendo-react-common';
import {
    Grid,
    GridColumn,
    GridColumnProps,
    GridColumnReorderEvent,
    GridContextMenuEvent,
    GridCustomCellProps,
    GridCustomHeaderCellProps,
    GridEvent,
    GridFilterChangeEvent,
    GridHandle,
    GridPageChangeEvent,
    GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { Skeleton } from '@progress/kendo-react-indicators';
import clsx from 'clsx';
import * as React from 'react';
import { ListState } from '../../../components/common/types';
import { BorderColorOutline, FilterOutline } from '../../../icons/icons';
import { queryFlightsGrid } from '../../../services/grid-query-service';
import { UDViewTagSelectionEnum } from '../../../udView/udview';
import { ColumnMenu } from '../../components/grid/ColumnMenu';
import { ColumnMenuContext } from '../../components/grid/ColumnMenuContext';
import { hasFilter } from '../../components/grid/helpers';
import { MonitorStatusBadge } from '../../components/monitor-status/MonitorStatusBadge';
import { EventBus } from '../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { useSafeEffect } from '../../hooks/use-safe-effect';
import { setSetting } from '../../state/slices/settings-slice';
import { store } from '../../state/store';

function createInitialDataStateAndOrder(all: GridColumnsWithExtra[]) {
    const settings = store.getState().settings;
    const savedState = settings.flightListState;
    const savedOrder = settings.flightListOrder;

    // TODO: walk the filter recursively and remove all filters that are not part of the displayed columns list
    // TODO: walk the sort recursively and remove all sorts that are not part of the displayed columns list

    return {
        filter: savedState?.filter,
        sort: savedState?.sort,
        columnState: savedOrder
            ? (savedOrder.map((saved) => all.find((x) => x.id === saved)).filter(Boolean) as GridColumnsWithExtra[])
            : all.filter((x) => x.default)
    };
}

export function FlightList() {
    const allColumns = _allColumns;
    const focusedWorkspaceId = useAppSelector((x) => x.workspace.selectedId);
    const filterMode = useAppSelector((state) => state.settings.flightListFilterMode);
    const storedGridFilterAndSort = useAppSelector((x) => x.settings.flightListState);
    const initialState = React.useMemo(() => createInitialDataStateAndOrder(allColumns), []);
    const lastUpdate = useAppSelector((x) => x.global.flightWorker.lastUpdate?.time);
    const [skip, setSkip] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(80);
    const [filter, setFilter] = React.useState(initialState.filter);
    const [sort, setSort] = React.useState(initialState.sort);
    const [total, setTotal] = React.useState(0);
    const [data, setData] = React.useState({ skip: 0, data: [] as any[] });
    const [columnState, setColumnState] = React.useState<GridColumnProps[]>(initialState.columnState);

    const columnStateRef = React.useRef<GridColumnProps[]>(initialState.columnState);
    const gridRef = React.useRef<GridHandle>(null);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        // TODO: This needs better logic. When should we autosize columns?
        gridRef.current?.fitColumns(initialState.columnState.map((x) => x.id) as string[]);
    }, [initialState]);

    useSafeEffect(() => {
        queryFlightsGrid({
            columns: (columnStateRef.current as GridColumnsWithExtra[]).map((x) => x.tagSelector),
            state: {
                filter,
                sort,
                skip,
                take: pageSize
            },
            // The next 3 props are used to calculate some offsets so that the list doesn't jump when you are at position e.g. 5000 and there were 30 aircraft remove at position 1-4999
            // Ignore this for now because the Grid is a hot mess atm.
            filterOrSortChanged: true, //filterOrSortChanged.current,
            previousResult: undefined,
            /*{
                ...resultRef.current,
                data: resultRef.current?.data?.map((x) => x.id)
            }*/
            previousSkip: 0, // pendingState.current.skip ?? 0,
            filterMode,
            focusedWorkspaceId
        }).then((x) => {
            setTotal(x.result.total);
            setData({ skip, data: x.result.data });
        });
    }, [skip, pageSize, filter, sort, filterMode, columnState, lastUpdate, focusedWorkspaceId]);

    const storeFilterAndSort = (state: Partial<ListState>) => {
        dispatch(setSetting('flightListState', { ...storedGridFilterAndSort, ...state }));
    };

    const onPageChange = (event: GridPageChangeEvent) => {
        // Telerik docs say to debounce this event
        // That's because Telerik maintains a cache of all data and doesn't ever invalidate it. We can't do that because flight list changes too often
        setSkip(event.page.skip);
        setPageSize(event.page.take);
    };

    const filterChanged = (event: GridFilterChangeEvent) => {
        setFilter(event.filter);
        storeFilterAndSort({ filter: event.filter });
    };

    const sortChanged = (event: GridSortChangeEvent) => {
        if (event.nativeEvent.shiftKey) {
            if (event.sort.length > 2) {
                event.sort.splice(1, 1);
            }
        } else {
            if (event.sort.length > 1) {
                event.sort.splice(0, event.sort.length - 1);
            }
        }

        setSort(event.sort);
        storeFilterAndSort({ sort: event.sort });
    };

    const saveOrder = React.useCallback((columns: GridColumnProps[]) => {
        const orderIds = columns.map((x) => x.id!);
        dispatch(setSetting('flightListOrder', orderIds));
    }, []);

    const columnOrderChanged = React.useCallback((event: GridColumnReorderEvent) => {
        setColumnState(event.columns);
        columnStateRef.current = event.columns;
        saveOrder(event.columns);
    }, []);

    const onColumnsChange = React.useCallback((cols: GridColumnProps[]) => {
        console.log(cols);
        setColumnState(cols);
        columnStateRef.current = cols;
        saveOrder(cols);
        self.setTimeout(() => gridRef.current?.fitColumns(cols.map((x) => x.id) as string[]), 0);
    }, []);

    const handleContextMenu = React.useCallback((event: GridContextMenuEvent) => {
        event.syntheticEvent.preventDefault();
        const item = event.dataItem;

        EventBus.fireContextMenu([{ kind: 'flight', flightIds: [item.id] }], event.nativeEvent);
    }, []);

    const dismissContextMenu = React.useCallback((event: GridEvent) => {
        EventBus.fireContextMenu([], event.nativeEvent);
    }, []);

    const displayData = React.useMemo(() => {
        /*
         * Long story short: grid will internally update its skip state even if you don't pass a new skip prop to it. This is different from what it used to do.
         * Previously we were able to wait with setting the skip prop until we have data and then set both at once. But now, the grid will update skip internally and re-render
         * with the data for previous skip shifted according to the new skip and shortly after that the real data will arrive creating a jigger effect.
         * Instead, we keep track of the data that we have and skip skip it belongs to. Here we calculate what's the delta between previous skip and this skip and we create
         * a data that copies items from the previous data array and places them in the new array according to the skip delta.
         * This will result in Grid not having all the data to display but at least nothing will shift unexpectedly. When the real data arrives those gaps will be filled in.
         */
        const itemCount = Math.min(pageSize, Math.max(total - skip, 0));
        const arr = new Array(itemCount);
        const skipDiff = skip - data.skip;
        for (let i = 0; i < arr.length; i++) {
            arr[i] = data.data[skipDiff + i] ?? { id: 50_000 + i, auto: true };
        }
        return arr;
    }, [data, pageSize, skip]);

    const Header = React.useMemo(() => getHeader(filter), [filter]);

    return (
        <ColumnMenuContext.Provider
            value={{
                columnsState: columnState,
                columns: allColumns,
                onColumnsChange
            }}>
            <Grid
                className="grid-hide-filter-row telerik-cobaltify"
                ref={gridRef}
                style={{ height: '100%', width: '100%' }}
                resizable={true}
                reorderable={true}
                sortable={{ mode: 'multiple', allowUnsort: true }}
                filterable={true}
                navigatable={true}
                scrollable={'virtual'}
                fixedScroll
                dataItemKey={'id'}
                rowHeight={30}
                total={total}
                onScroll={dismissContextMenu}
                onContextMenu={handleContextMenu}
                onFilterChange={filterChanged}
                onSortChange={sortChanged}
                columnMenu={ColumnMenu}
                onColumnReorder={columnOrderChanged}
                onPageChange={onPageChange}
                skip={skip}
                pageSize={pageSize}
                data={displayData}
                filter={filter}
                cells={{
                    headerCell: Header
                }}
                sort={sort}>
                {columnState.map((c) => (
                    <GridColumn key={c.id} {...c} />
                ))}
            </Grid>
        </ColumnMenuContext.Provider>
    );
}

function createCell(column: GridColumnsWithExtra) {
    const getMainCell = (props: GridCustomCellProps) => {
        if (!props.dataItem || props.dataItem.auto) {
            return (
                <td {...props.tdProps}>
                    <Skeleton className="w-100" shape="text" />
                </td>
            );
        }
        const className = column.justify === 'right' ? 'text-end pe-4' : '';
        if (column.tagSelector == UDViewTagSelectionEnum.MonitorStatus) {
            return (
                <td {...props.tdProps}>
                    <div className="d-flex">
                        <MonitorStatusBadge monitorStatus={MonitorStatus[props.dataItem[column.tagSelector]] as unknown as MonitorStatus} />
                    </div>
                </td>
            );
        }
        if (column.tagSelector == UDViewTagSelectionEnum.Notes) {
            return (
                <td {...props.tdProps} className={clsx(props.className, className)}>
                    {props.dataItem[column.tagSelector] && <SvgIcon icon={BorderColorOutline} />}
                </td>
            );
        }
        return <td {...props.tdProps}>{props.dataItem[column.tagSelector]}</td>;
    };

    return (props: GridCustomCellProps) => {
        return (
            <>
                {props.tdProps && getMainCell(props)}
                {props.td2Props && <td {...props.td2Props} />}
            </>
        );
    };
}

type GridColumnsWithExtra = GridColumnProps & {
    tagSelector: UDViewTagSelectionEnum;
    justify?: 'right' | 'left';
    default?: boolean;
};

const _allColumns: GridColumnsWithExtra[] = [
    {
        id: 'hasNotes',
        field: 'hasNotes',
        title: 'Notes',
        tagSelector: UDViewTagSelectionEnum.Notes,
        default: true
    },
    {
        id: 'acid',
        field: 'normalizedAcid',
        title: 'Aircraft ID',
        tagSelector: UDViewTagSelectionEnum.Acid,
        default: true
    },
    {
        id: 'monStat',
        field: 'monitorStatus',
        title: 'MonStat',
        tagSelector: UDViewTagSelectionEnum.MonitorStatus,
        default: true
    },
    {
        id: 'origin',
        field: 'origin',
        title: 'Origin',
        tagSelector: UDViewTagSelectionEnum.Origin,
        default: true
    },
    {
        id: 'destination',
        field: 'destination',
        title: 'Dest',
        tagSelector: UDViewTagSelectionEnum.Destination,
        default: true
    },
    {
        id: 'acType',
        field: 'aircraftType',
        title: 'AcType',
        tagSelector: UDViewTagSelectionEnum.AircraftType,
        default: true
    },
    {
        id: 'altitude',
        filter: 'numeric',
        field: 'altitude',
        title: 'Altitude',
        tagSelector: UDViewTagSelectionEnum.Altitude,
        default: true
    },
    {
        id: 'speed',
        filter: 'numeric',
        field: 'speed',
        title: 'Speed',
        tagSelector: UDViewTagSelectionEnum.Speed,
        default: true
    },
    {
        id: 'depTime',
        field: 'departureTime',
        title: 'DepTime',
        tagSelector: UDViewTagSelectionEnum.DepTimeEtd,
        default: true
    },
    {
        id: 'etaFAA',
        field: 'arrivalTime',
        title: 'ETA(FAA)',
        tagSelector: UDViewTagSelectionEnum.EtaFaa,
        default: true
    },
    {
        id: 'edct',
        field: 'extra.edct',
        title: 'CTD/EDCT',
        tagSelector: UDViewTagSelectionEnum.CtdEdct,
        default: true
    },
    {
        id: 'flightStatus',
        field: 'status',
        title: 'Status',
        tagSelector: UDViewTagSelectionEnum.Status,
        default: true
    },
    {
        id: 'lat',
        field: 'position.lat',
        title: 'Latitude',
        filter: 'numeric',
        tagSelector: UDViewTagSelectionEnum.Latitude,
        default: true
    },
    {
        id: 'lng',
        field: 'position.lng',
        title: 'Longitude',
        filter: 'numeric',
        tagSelector: UDViewTagSelectionEnum.Longitude,
        default: true
    },
    {
        id: 'modesid',
        field: 'icao',
        title: 'Mode-S ID',
        tagSelector: UDViewTagSelectionEnum.ModeS,
        default: true
    },
    {
        id: 'tailNo',
        field: 'registrationId',
        title: 'Tail #',
        tagSelector: UDViewTagSelectionEnum.TailNumber,
        default: true
    },
    {
        id: 'dsttodest',
        field: 'distanceToDestination',
        title: 'Dist To Dest',
        tagSelector: UDViewTagSelectionEnum.DistanceToDestination,
        default: true
    }, ///TO DO Made text change for title
    {
        id: 'objectclass',
        field: 'class',
        title: 'Object class',
        tagSelector: UDViewTagSelectionEnum.ObjectClass,
        default: true
    },
    {
        id: 'holding',
        field: 'holding',
        title: 'Holding',
        tagSelector: UDViewTagSelectionEnum.Holding,
        default: true
    },
    {
        id: 'altdest',
        field: 'extra.destinationAlternateAirport1',
        title: 'Alt dest',
        tagSelector: UDViewTagSelectionEnum.AlternateDestination
    },
    {
        id: 'altdest2',
        field: 'extra.destinationAlternateAirport2',
        title: 'Alt dest 2',
        tagSelector: UDViewTagSelectionEnum.AlternateDestination2
    },
    {
        id: 'arrctr',
        field: 'extra.arrivalCenter',
        title: 'Arr ctr',
        tagSelector: UDViewTagSelectionEnum.ArrivalCenter
    },
    {
        id: 'arrdev',
        field: 'arrivalDeviation',
        title: 'Arr dev',
        tagSelector: UDViewTagSelectionEnum.ArrivalDev
    },
    {
        id: 'beacon',
        field: 'beacon',
        title: 'Beacon',
        tagSelector: UDViewTagSelectionEnum.Beacon
    },
    {
        id: 'calcaltplan',
        field: 'extra.plannedAltitude.value',
        title: 'Calc alt plan',
        tagSelector: UDViewTagSelectionEnum.DevAltPlan
    },
    {
        id: 'calcfuelplan',
        field: 'extra.plannedFuelOnBoard.value',
        title: 'Calc fuel plan',
        tagSelector: UDViewTagSelectionEnum.DevFobPlan
    },
    {
        id: 'cete',
        field: 'extra.cete',
        title: 'CETE',
        tagSelector: UDViewTagSelectionEnum.CETE
    },
    {
        id: 'ctrlarrdel',
        field: 'controlledArrivalDelay',
        title: 'Ctl arr del',
        tagSelector: UDViewTagSelectionEnum.ControlArrivalDel
    },
    {
        id: 'ctrdepdel',
        field: 'controlledDepartureDelay',
        title: 'Ctl dep del',
        tagSelector: UDViewTagSelectionEnum.ControlDepDel
    },
    {
        id: 'ctrlelem',
        field: 'extra.controlElement',
        title: 'Cont Elem',
        tagSelector: UDViewTagSelectionEnum.ControlElement
    },
    {
        id: 'crewid',
        field: 'extra.crewId',
        title: 'Crew ID',
        tagSelector: UDViewTagSelectionEnum.CrewId
    },
    {
        id: 'cta',
        field: 'extra.cta',
        title: 'CTA',
        tagSelector: UDViewTagSelectionEnum.CTA
    },
    {
        id: 'depctr',
        field: 'extra.departureCenter',
        title: 'Dep ctr',
        tagSelector: UDViewTagSelectionEnum.DepartureCenter
    },
    {
        id: 'depdev',
        field: 'departureDeviation',
        title: 'Dep dev',
        tagSelector: UDViewTagSelectionEnum.DepartureDev
    },
    {
        id: 'destchg',
        field: 'oldDestination',
        title: 'Dest chg',
        tagSelector: UDViewTagSelectionEnum.DestinationChange
    },
    {
        id: 'destgate',
        field: 'extra.destinationGate',
        title: 'Dst gate',
        tagSelector: UDViewTagSelectionEnum.DestinationGate
    },
    {
        id: 'offrt',
        field: 'milesOffRoute',
        title: 'Dst Off Rt',
        tagSelector: UDViewTagSelectionEnum.DistanceOffRoute
    }, //TO DO Made text change for title
    {
        id: 'diverting',
        field: 'diverting',
        title: 'Diverting',
        tagSelector: UDViewTagSelectionEnum.Diverting
    },
    {
        id: 'divrcvrypri',
        field: 'extra.recoverPriority',
        title: 'Div rcvry pri',
        tagSelector: UDViewTagSelectionEnum.Divrecpri
    },
    {
        id: 'eta',
        field: 'etaFe',
        title: 'ETA(FE)',
        tagSelector: UDViewTagSelectionEnum.EtaFe
    },
    {
        id: 'ete',
        field: 'ete',
        title: 'ETE',
        tagSelector: UDViewTagSelectionEnum.ETE
    },
    {
        id: 'etedev',
        field: 'eteDeviation',
        title: 'ETE dev',
        tagSelector: UDViewTagSelectionEnum.EtedEvFromO
    },
    {
        id: 'etrfaa',
        field: 'etrFAA',
        title: 'ETR',
        tagSelector: UDViewTagSelectionEnum.EtrFaa
    },
    {
        id: 'etrfe',
        field: 'etrFE',
        title: 'ETR(FE)',
        tagSelector: UDViewTagSelectionEnum.EtrFe
    },
    {
        id: 'event',
        field: 'eventTODO',
        title: 'Event',
        tagSelector: UDViewTagSelectionEnum.EventText
    }, //TO DO
    {
        id: 'fob',
        field: 'extra.fuelOnBoard',
        title: 'FOB',
        tagSelector: UDViewTagSelectionEnum.FuelOnBoard
    },
    {
        id: 'fob-time',
        field: 'extra.fuelOnBoardTime',
        title: 'FOB Time',
        tagSelector: UDViewTagSelectionEnum.FuelOnBoardTime
    },
    {
        id: 'goaround',
        field: 'goAround',
        title: 'Go Around',
        tagSelector: UDViewTagSelectionEnum.GoAround
    },
    {
        id: 'impacts',
        field: 'impacts.size',
        title: 'Impact(s)',
        tagSelector: UDViewTagSelectionEnum.Impacts
    }, //TO DO
    {
        id: 'impactsafm',
        field: 'impacts.size',
        title: 'Impacts (AFM)',
        tagSelector: UDViewTagSelectionEnum.ImpactAFM
    }, //TO DO is this the same impacts list above .. Impact (AFM) Only do the 3 AFM impacts Reroutes,FCA/FEA, Airport delays
    {
        id: 'in',
        field: 'extra.in',
        title: 'IN',
        tagSelector: UDViewTagSelectionEnum.In
    },
    {
        id: 'intdelayreason',
        field: 'extra.initialDelay',
        title: 'Init del rsn',
        tagSelector: UDViewTagSelectionEnum.InitialDelayReason
    },
    {
        id: 'irrop',
        field: 'extra.irregularOperations',
        title: 'Irr Op',
        tagSelector: UDViewTagSelectionEnum.IrregularOperations
    },
    {
        id: 'lastdelayreason',
        field: 'extra.lastDelay',
        title: 'Last del rsn',
        tagSelector: UDViewTagSelectionEnum.LastDelayReason
    },
    {
        id: 'lastdst',
        field: 'oldDestination',
        title: 'Last dest',
        tagSelector: UDViewTagSelectionEnum.LastDestination
    },
    {
        id: 'lastpostime',
        field: 'lastPositionReportTime',
        title: 'Last pos time',
        tagSelector: UDViewTagSelectionEnum.LastPositionTime
    },
    {
        id: 'nonctrlarrdelay',
        field: 'nonControlledArrivalDelay',
        title: 'NCtl arr del',
        tagSelector: UDViewTagSelectionEnum.NonCtlArrivalDel
    },
    {
        id: 'nonctrldepdelay',
        field: 'nonControlledDepartureDelay',
        title: 'NCtl dep del',
        tagSelector: UDViewTagSelectionEnum.NonCtlDepDel
    },
    {
        id: 'nose',
        field: 'extra.noseNumber',
        title: 'Nose #',
        tagSelector: UDViewTagSelectionEnum.NoseNumber
    },
    {
        id: 'numseat',
        field: 'extra.numberOfSeats',
        title: 'Num seats',
        tagSelector: UDViewTagSelectionEnum.NumSeats
    },
    {
        id: 'numpass',
        field: 'extra.numberOfPassengers',
        title: 'Numn pass',
        tagSelector: UDViewTagSelectionEnum.NumPassanger
    },
    {
        id: 'numcrew',
        field: 'extra.numberOfCrew',
        title: 'Num crew',
        tagSelector: UDViewTagSelectionEnum.NumCrew
    },
    {
        id: 'oeta',
        field: 'oeta',
        title: 'OETA',
        tagSelector: UDViewTagSelectionEnum.OETA
    },
    {
        id: 'oete',
        field: 'oete',
        title: 'OETE',
        tagSelector: UDViewTagSelectionEnum.OETE
    },
    {
        id: 'oetd',
        field: 'oetd',
        title: 'OETD',
        tagSelector: UDViewTagSelectionEnum.OETD
    },
    {
        id: 'off',
        field: 'extra.off',
        title: 'OFF',
        tagSelector: UDViewTagSelectionEnum.Off
    },
    {
        id: 'orggate',
        field: 'extra.originGate',
        title: 'Org gate',
        tagSelector: UDViewTagSelectionEnum.OriginGate
    },
    {
        id: 'on',
        field: 'extra.on',
        title: 'ON',
        tagSelector: UDViewTagSelectionEnum.On
    },
    {
        id: 'out',
        field: 'extra.out',
        title: 'OUT',
        tagSelector: UDViewTagSelectionEnum.Out
    },
    {
        id: 'pairedfix',
        field: 'pairedFix',
        title: 'Paired fix',
        tagSelector: UDViewTagSelectionEnum.Pairedfixid
    },
    {
        id: 'peta',
        field: 'plannedArrivalTime',
        title: 'PETA',
        tagSelector: UDViewTagSelectionEnum.PETA
    },
    {
        id: 'pete',
        field: 'pete',
        title: 'PETE',
        tagSelector: UDViewTagSelectionEnum.PETE
    },
    {
        id: 'petd',
        field: 'plannedDepartureTime',
        title: 'PETD',
        tagSelector: UDViewTagSelectionEnum.PETD
    },
    {
        id: 'plancruisealt',
        field: 'plannedAltitude',
        title: 'Pln cr alt',
        tagSelector: UDViewTagSelectionEnum.PlannedAltitude
    }, //TO DO duplicate Second attempt
    {
        id: 'plancruisespd',
        field: 'plannedSpeed',
        title: 'Pln cr spd',
        tagSelector: UDViewTagSelectionEnum.PlannedSpeed
    }, //TO DO duplicate Second attempt
    {
        id: 'possorce',
        field: 'feedId',
        title: 'Pos src',
        tagSelector: UDViewTagSelectionEnum.PositionSource
    },
    {
        id: 'priority',
        field: 'priority',
        title: 'Priority',
        tagSelector: UDViewTagSelectionEnum.Priority
    },
    {
        id: 'reservefuel',
        field: 'extra.fuelReserve',
        title: 'Res fuel',
        tagSelector: UDViewTagSelectionEnum.FuelReserve
    },
    {
        id: 'route',
        field: 'milesOffRouteTODO',
        title: 'Route',
        tagSelector: UDViewTagSelectionEnum.RouteText
    },
    {
        id: 'runway',
        field: 'runway',
        title: 'Runway',
        tagSelector: UDViewTagSelectionEnum.Runway
    },
    {
        id: 'scratchpad',
        field: 'extra.scratchpad',
        title: 'Scratchpad',
        tagSelector: UDViewTagSelectionEnum.Scratchpad
    },
    {
        id: 'sctordeskid',
        field: 'extra.userField1',
        title: 'Sector/Desk ID',
        tagSelector: UDViewTagSelectionEnum.SectordeskId
    },
    {
        id: 'sinceposupdt',
        field: 'lastPositionReportTime',
        title: 'Since pos updt',
        tagSelector: UDViewTagSelectionEnum.SincePositionUpdate
    }, //TO DO check if the sorting works
    {
        id: 'taxitime',
        field: 'extra.taxiTime',
        title: 'Taxi time',
        tagSelector: UDViewTagSelectionEnum.TaxiTime
    },
    {
        id: 'toa',
        field: 'extra.toa',
        title: 'TOA',
        tagSelector: UDViewTagSelectionEnum.TOA
    },
    {
        id: 'track',
        field: 'heading',
        title: 'Track',
        tagSelector: UDViewTagSelectionEnum.Track
    },
    {
        id: 'userfield2',
        field: 'extra.userField2',
        title: 'User field 2',
        tagSelector: UDViewTagSelectionEnum.UserField2
    },
    {
        id: 'altDestinationOther',
        field: 'extra.altDestTODO',
        title: 'Alt dest othr',
        tagSelector: UDViewTagSelectionEnum.AlternateDestinationOther
    },
    {
        id: 'etopsAirports',
        field: 'extra.etopsAltTOTO',
        title: 'ETOPS alts',
        tagSelector: UDViewTagSelectionEnum.etopsAlts
    }
] as const;

_allColumns.forEach((x) => (x.cells = { data: createCell(x) }));

function getHeader(filter: CompositeFilterDescriptor | undefined) {
    return function (props: GridCustomHeaderCellProps) {
        return (
            <th {...props.thProps}>
                <div className="d-flex gap-1 align-items-center">
                    {hasFilter(filter, props.field) && <SvgIcon className="icon-b-1" size="small" icon={FilterOutline} />}
                    {props.children}
                </div>
            </th>
        );
    };
}
